import type { SetType } from '@cadencia/contracts';
import { findExercise, requireExercise } from '../catalog/exercises.js';
import { MUSCLES, type Muscle } from '../catalog/muscles.js';

/**
 * Contabilidad de volumen por musculo y bandas semanales
 * (docs/TRAINING_ENGINE.md §4.1 y §4.2).
 *
 * Es el cimiento de la Fase 3: el generador de bloques, el ajuste por respuesta
 * y el deload deciden todos a partir de "cuantas series efectivas lleva este
 * musculo esta semana". Vive en el dominio y es puro: sin reloj, sin IO y sin
 * aleatoriedad (ADR-007).
 */

// ---------------------------------------------------------------------------
// Contabilidad
// ---------------------------------------------------------------------------

/**
 * Peso de una serie segun su tipo, en series efectivas.
 *
 * INTERPRETACION del documento, declarada aqui para que se pueda discutir: la
 * regla dice que un drop set cuenta como "1 serie efectiva + 0,5 por cada
 * drop". Como cada fila de `set_prescriptions` lleva su propio `set_type`, se
 * lee fila a fila: la serie base ya cuenta 1,0 por si misma y cada fila `drop`
 * añade su 0,5. Rest-pause y myo-reps son una sola fila que vale 1,5.
 *
 * Los calentamientos no aparecen aqui porque no pueden: viven en
 * `warmup_prescriptions`, una tabla distinta (ADR-013). Que sea imposible
 * contarlos es mejor garantia que acordarse de excluirlos.
 */
export function setTypeWeight(setType: SetType): number {
  switch (setType) {
    case 'effective':
    case 'top':
    case 'backoff':
      return 1;
    case 'drop':
      return 0.5;
    case 'rest_pause':
    case 'myo':
      return 1.5;
    default: {
      const exhaustive: never = setType;
      throw new Error(`Tipo de serie no contemplado: ${String(exhaustive)}`);
    }
  }
}

/** Reparto de una serie entre los musculos del ejercicio (§4.1). */
const PRIMARY_SHARE = 1;
const SECONDARY_SHARE = 0.5;

/** Lo minimo que hace falta saber de una serie para contarla. */
export interface CountableSet {
  exerciseSlug: string;
  setType: SetType;
}

export interface VolumeCount {
  /** Series efectivas atribuidas a cada musculo. Solo los que tienen volumen. */
  perMuscle: Partial<Record<Muscle, number>>;
  /** Series efectivas contadas, antes de repartir por musculo. */
  effectiveSets: number;
  /**
   * Slugs que no estan en el catalogo. Su volumen NO se ha contado.
   *
   * Se devuelven en lugar de ignorarse en silencio: un slug mal escrito haria
   * que un musculo pareciera por debajo de su banda, y la respuesta del motor a
   * eso es AÑADIR volumen. Un fallo silencioso aqui no da un numero raro, da
   * mas series de las que tocan.
   */
  unknownSlugs: string[];
}

/**
 * Cuenta el volumen de un conjunto de series ya realizadas o prescritas.
 *
 * No sabe de semanas ni de fechas: quien llama decide que series entran. Asi la
 * misma funcion sirve para una sesion, para una semana o para simular un bloque
 * antes de crearlo.
 */
export function countVolume(sets: readonly CountableSet[]): VolumeCount {
  const perMuscle: Partial<Record<Muscle, number>> = {};
  const unknown = new Set<string>();
  let effectiveSets = 0;

  const add = (muscle: Muscle, amount: number): void => {
    perMuscle[muscle] = (perMuscle[muscle] ?? 0) + amount;
  };

  for (const set of sets) {
    const exercise = findExercise(set.exerciseSlug);
    if (!exercise) {
      unknown.add(set.exerciseSlug);
      continue;
    }

    const weight = setTypeWeight(set.setType);
    effectiveSets += weight;

    for (const muscle of exercise.primaryMuscles) add(muscle, weight * PRIMARY_SHARE);
    for (const muscle of exercise.secondaryMuscles) add(muscle, weight * SECONDARY_SHARE);
  }

  // Todos los pesos son multiplos de 0,25, exactamente representables en coma
  // flotante binaria: la suma no acumula error y no hace falta redondear.
  return { perMuscle, effectiveSets, unknownSlugs: [...unknown].sort() };
}

/**
 * Series efectivas ya hechas hoy que CARGAN LOS MUSCULOS de este ejercicio.
 *
 * Es lo que `sessionSetsBefore` significa en `recommend.ts`, y no lo que se le
 * estaba pasando: la aplicacion le daba el total de series de la sesion, asi
 * que once series de pecho y espalda le metian fatiga a la primera de
 * cuadriceps, que estaban frescos. El multiplicador solo empieza a morder a
 * partir de la quinta serie, de modo que el efecto era exactamente el de
 * penalizar lo que va al final de la sesion por estar al final.
 *
 * QUE CUENTA COMO «EL MISMO MUSCULO». Los PRIMARIOS del ejercicio que se va a
 * prescribir. La pregunta que responde el numero es «cuanto llevan encima los
 * musculos que voy a entrenar ahora», y el primario es el que decide si la
 * serie sale o no.
 *
 * Las series previas, en cambio, se reparten con la contabilidad de siempre:
 * `countVolume` ya atribuye 1,0 al primario y 0,5 al secundario y ya pesa cada
 * fila con `setTypeWeight`. Asi un press de banca previo aporta a triceps la
 * mitad —trabajo hubo, pero no era el protagonista— sin que este modulo repita
 * ni un solo factor. Duplicarlos aqui seria la segunda copia de una regla que
 * ya tiene dueño.
 *
 * Y el MAXIMO entre los primarios, no la suma: en un ejercicio de dos primarios
 * —fondos en paralelas, pectoral y triceps— sumar contaria dos veces cada serie
 * anterior e inventaria una fatiga que nadie ha hecho. El maximo responde «el
 * mas cargado de los musculos que voy a usar», que es el que manda.
 *
 * POLITICA DE SLUGS DESCONOCIDOS. La del dominio, sin inventar una nueva: para
 * el ejercicio objetivo `requireExercise`, porque sin catalogo no hay musculos
 * que mirar y prescribir a ciegas no es una opcion; para las series ya hechas
 * la de `countVolume`, que las deja fuera del recuento en vez de estallar.
 */
export function sameMuscleSetsBefore(
  exerciseSlug: string,
  setsBefore: readonly CountableSet[],
): number {
  const target = requireExercise(exerciseSlug);
  const { perMuscle } = countVolume(setsBefore);

  let max = 0;
  for (const muscle of target.primaryMuscles) {
    max = Math.max(max, perMuscle[muscle] ?? 0);
  }
  return max;
}

// ---------------------------------------------------------------------------
// Bandas
// ---------------------------------------------------------------------------

/**
 * Banda semanal de un musculo, en series efectivas.
 *
 * El objetivo es un rango y no un numero porque el documento lo define asi
 * (14-18, 12-16...). Aplanarlo a un valor unico obligaria a inventar una
 * precision que la evidencia no tiene.
 */
export interface VolumeBand {
  min: number;
  targetMin: number;
  targetMax: number;
  max: number;
}

const GRANDE: VolumeBand = { min: 10, targetMin: 14, targetMax: 18, max: 22 };
const MEDIO: VolumeBand = { min: 8, targetMin: 12, targetMax: 16, max: 20 };
const PEQUENO: VolumeBand = { min: 6, targetMin: 8, targetMax: 12, max: 16 };
/** Musculos que reciben casi todo su volumen de forma indirecta. */
const INDIRECTO: VolumeBand = { min: 0, targetMin: 4, targetMax: 8, max: 12 };

/**
 * Bandas de partida (§4.2). El documento es explicito para unos musculos y
 * agrupa otros; donde agrupa, la asignacion concreta es INTERPRETACION y esta
 * marcada como tal. El propio documento advierte que estos valores son solo el
 * punto de partida y que lo que importa es la recalibracion por respuesta
 * observada (§4.3, entregable 3.3 posterior).
 */
export const DEFAULT_VOLUME_BANDS: Record<Muscle, VolumeBand> = {
  // --- del documento, literal ---
  pectoral: GRANDE,
  cuadriceps: GRANDE,
  isquios: GRANDE,
  deltoide_lateral: MEDIO,
  deltoide_posterior: MEDIO,
  biceps: MEDIO,
  triceps: MEDIO,
  gluteo: MEDIO,
  gemelo: PEQUENO,
  core: PEQUENO,

  // --- interpretacion: "Espalda" es una sola entrada en la tabla ---
  // Se asigna la banda grande al dorsal, que es el primario de todo tiron.
  dorsal: GRANDE,
  // REVISAR: trapecio y romboides acumulan mucho volumen indirecto en remos y
  // dominadas. Darles banda grande dispararia el total de la espalda.
  trapecio: PEQUENO,
  romboides: PEQUENO,

  // --- interpretacion: musculos ausentes de la tabla ---
  // El documento nombra "Hombro (lateral/posterior)" y deja fuera el anterior a
  // proposito: recibe volumen de todo empuje. Banda propia y menor.
  deltoide_anterior: PEQUENO,
  // REVISAR: el antebrazo trabaja en cada tiron; rara vez necesita trabajo
  // directo. Lo mismo para los aductores en sentadillas y zancadas.
  antebrazo: INDIRECTO,
  aductores: INDIRECTO,
  soleo: PEQUENO,
  lumbar: PEQUENO,
};

export function bandFor(
  muscle: Muscle,
  overrides: Partial<Record<Muscle, VolumeBand>> = {},
): VolumeBand {
  return overrides[muscle] ?? DEFAULT_VOLUME_BANDS[muscle];
}

// ---------------------------------------------------------------------------
// Diagnostico
// ---------------------------------------------------------------------------

export type VolumeStatus = 'below_min' | 'under_target' | 'in_target' | 'over_target' | 'above_max';

/**
 * Situa un volumen dentro de su banda.
 *
 * `over_target` y `above_max` son estados distintos a proposito: pasarse del
 * objetivo es una señal para dejar de subir, superar el maximo es una señal
 * para bajar. Fundirlos en "demasiado" perderia esa diferencia.
 */
export function classifyVolume(sets: number, band: VolumeBand): VolumeStatus {
  if (sets < band.min) return 'below_min';
  if (sets < band.targetMin) return 'under_target';
  if (sets <= band.targetMax) return 'in_target';
  if (sets <= band.max) return 'over_target';
  return 'above_max';
}

export interface MuscleVolumeReport {
  muscle: Muscle;
  sets: number;
  band: VolumeBand;
  status: VolumeStatus;
}

/**
 * Informe por musculo, con TODOS los musculos del vocabulario.
 *
 * Incluye los que estan a cero a proposito: un musculo sin trabajar no aparece
 * en el recuento, y si el informe solo listara lo contado, quedarse sin
 * entrenar un grupo entero seria justo lo que no se ve.
 */
export function volumeReport(
  count: VolumeCount,
  overrides: Partial<Record<Muscle, VolumeBand>> = {},
): MuscleVolumeReport[] {
  return MUSCLES.map((muscle) => {
    const sets = count.perMuscle[muscle] ?? 0;
    const band = bandFor(muscle, overrides);
    return { muscle, sets, band, status: classifyVolume(sets, band) };
  });
}

/** Musculos fuera de banda, que son los que piden una decision. */
export function outOfBand(report: readonly MuscleVolumeReport[]): MuscleVolumeReport[] {
  return report.filter((entry) => entry.status === 'below_min' || entry.status === 'above_max');
}
